import apiClient, { uiAPI } from 'lib/apiClient';
import moment from 'moment';
import { updateSubscription } from './subscriptions.service';

export const createAccount = (payload) =>
  apiClient.post('API/1_0/account.php', payload);

export const getAccount = (account_id) =>
  apiClient
    .get('API/1_0/account.php', {
      params: {
        account_id,
        limit: 1,
        load_subscriptions: 1,
        load_payment_methods: 1,
        load_invoices: 0,
        load_connections: 1,
      },
    })
    .then((r) => r[0]);

export const getAccounts = (query = {}) =>
  apiClient.get('API/1_0/account.php', {
    params: query,
  });

export const getAccountUsage = (id) =>
  apiClient.get('API/1_0/getAccountUsageStats.php', {
    params: { account_id: id },
  });

export const updateAccount = (payload) =>
  apiClient.post('API/1_0/account.php', payload);

export const provisionNumber = (region, phone_number, connection_name) =>
  apiClient.post('API/1_0/provisionNumber.php', {
    region,
    phone_number,
    connection_name,
  });

export const getAccountLinks = () =>
  apiClient
    .get('API/1_0/link.php', { params: { anything: 1 } })
    .then((r) => r[0]);

export const updateVariables = (values) =>
  apiClient.post('API/1_0/link.php', values);

export const addCard = (payload) =>
  apiClient.post('API/1_0/stripePaymentMethod.php', payload);

export const updateCard = (stripe_id, card_token) =>
  apiClient.post('API/1_0/stripeCustomer.php', {
    id: stripe_id,
    source: card_token,
  });

export const loadInvoices = (customer, limit = 100) =>
  apiClient.get('API/1_0/invoice.php', { params: { limit, customer } });

export const loadInvoiceById = (customer, id) =>
  apiClient.get('API/1_0/invoice.php', { params: { id } });

export const loadUpcomingInvoice = (customer) =>
  apiClient.get('API/1_0/invoice.php', {
    params: {
      customer,
      upcoming: 1,
    },
  });

export const invoiceOverages = (params) =>
  apiClient.get('API/1_0/invoiceOverages.php', {
    params,
  });

export const addProduct = (product) =>
  apiClient.post('API/1_0/subscriptionItem.php', { plan: product });

export const removeProduct = (product) =>
  apiClient.delete('API/1_0/subscriptionItem.php', {
    params: {
      plan: product,
    },
  });

export const getProduct = (id) =>
  apiClient.get('API/1_0/product.php', { params: { id } });

export const loadPlans = (brand, current_plan) =>
  apiClient.get('API/1_0/getPlanList.php', {
    params: {
      limit: 100,
      product_brand: brand,
      is_upgrade: current_plan ? '1' : '0',
      current_plan,
    },
  });

export const previewUpgrade = async (stripeId, currentPlan, newPlan) => {
  const prorateDate = Date.now().valueOf();
  const plan = apiClient.get('API/1_0/subscription.php', {
    params: { id: currentPlan },
  });
  const upcomingInvoice = await loadUpcomingInvoice(stripeId);

  let cost = 0;
  upcomingInvoice.lines.data.forEach((line) => {
    if (line.period.start === prorateDate) {
      cost = cost + line.amount;
    }
  });
};

export const getIntegrations = () =>
  apiClient.get('API/1_0/accountIntegration.php?f=1');

export const getIntegration = (id) =>
  apiClient.get('API/1_0/accountIntegration.php', { params: {} });

export const saveIntegration = (data) =>
  apiClient.post('API/1_0/accountIntegration.php', data);

export const queueSync = (type, accountId, group_id) =>
  apiClient.post('API/1_0/postRabbitMQ.php', {
    type,
    group_id,
    account_id: accountId,
    sync_now: true,
  });

export const getSyncStatus = (type) =>
  apiClient.get('API/1_0/getRabbitMQ.php', {
    params: { queue: 'RABBITMQ_SYNC_QUEUE_NAME' },
  });

export const pauseAccount = (duration) =>
  apiClient.post('API/1_0/postAccountPause.php', { pause_months: duration });

export const cancelAccount = (accountHash) =>
  apiClient.post('API/1_0/postAccountCancel.php', { hash: accountHash });

export const getTwillioNumbers = (countryCode, areaCode) =>
  apiClient.get('API/1_0/provisionNumber.php', {
    params: { area_code: areaCode, region: countryCode },
  });

export const endTrial = async (account) => {
  const promises = [];
  account.subscriptions.forEach((s) => {
    if (s.status === 'trialing') {
      promises.push(updateSubscription(s.id, { trial_end: 'now' }));
    }
  });
  await Promise.all(promises);
  await updateAccount({
    hash: account.hash,
    account_bill_day: moment.utc().date(),
    account_bill_month: moment.utc().month() + 1,
    account_trial_end_date: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
    account_is_paused: 0,
    account_status: 'active',
  });
};

export const restartAccount = () =>
  apiClient.post('API/1_0/postAccountRestart.php');

export const postUpsells = (accountHash, items) =>
  apiClient.post('API/1_0/postUpsells.php', {
    hash: accountHash,
    offers: items,
  });

export const createKickstartRequest = (data, name = 'kickstart') =>
  apiClient.post('API/1_0/formResponse.php', {
    account_id: data.account_id,
    form_response: JSON.stringify(data),
    form_name: name,
    file_url: data.csv_import,
    form_response_file_type: data.csv_import && 'csv',
  });

export const getDashboardData = (query) =>
  apiClient.get('API/1_0/getAccountDashboard.php', {
    params: query,
  });

export const getAccountStats = (query) =>
  apiClient.get('API/1_0/getAccountStats.php', {
    params: query,
  });

export const getStripeCustomer = (id) =>
  apiClient.get('API/1_0/stripeCustomer.php', { params: { id } });

export const updateStripeCustomer = (stripeId, values) => {
  return apiClient.post('API/1_0/stripeCustomer.php', {
    id: stripeId,
    ...values,
  });
};

export const addBankAccount = (source) =>
  apiClient.post('API/1_0/bankAccount.php', { source });

export const verifyBankAccount = (source_id, amounts) =>
  apiClient.post('API/1_0/postStripeBankAccountVerify.php', {
    source_id,
    amounts,
  });

export const deleteBankAccount = (source) =>
  apiClient.delete('API/1_0/bankAccount.php', { params: { id: source } });

export const getVariables = (params) =>
  apiClient.get('API/1_0/accountVariables.php', { params });

export const saveVariable = (data) =>
  apiClient.post('API/1_0/accountVariables.php', data);

export const deleteVariable = (hash) =>
  apiClient.delete('API/1_0/accountVariables.php', { params: { hash } });

export const getConnections = (params) =>
  apiClient.get('API/1_0/connection.php', {
    params,
  });

export const updateConnection = (values) =>
  apiClient.post('API/1_0/connection.php', values);

export const deleteConnection = (hash) =>
  apiClient.delete('API/1_0/connection.php', { params: { hash } });

export const getAccountFeatures = (account_id) =>
  uiAPI.get('account/features', { account_id });

export const getCustomColumns = (account_hash) =>
  apiClient.get('API/1_0/contactCustomColumns.php', {
    params: { account_hash },
  });

export const getCustomColumn = (hash) =>
  apiClient
    .get('API/1_0/contactCustomColumns.php', { params: { hash } })
    .then((r) => r[0]);

export const addCustomColumn = (values) =>
  apiClient.post('API/1_0/contactCustomColumns.php', values);

export const deleteCustomColumn = (hash) =>
  apiClient.delete('API/1_0/contactCustomColumns.php', { params: { hash } });

export const addAccountNote = (values) =>
  apiClient.post('API/1_0/accountNotes.php', values);

export const getAccountNotes = (account_id) =>
  apiClient.get('API/1_0/accountNotes.php', { params: { account_id } });

// sender profile
export const getAccountEIN = (query) =>
  apiClient.get(`API/1_0/accountEINSearch.php`, { params: query });

export const getAllMessageApproval = (params) =>
  apiClient.get(`API/1_0/messageApprovals.php`, { params });

export const getMessageApproval = (values) =>
  apiClient.get(`API/1_0/messageApprovals.php`, { params: values });

export const getMessageDetail = (values) =>
  apiClient.post('API/1_0/messageApprovals.php', values);

export const getAssessmentReport = (account_id) =>
  apiClient.get('API/1_0/accountAssessments.php', {
    params: { account_id },
  });

export const getStatusList = () => apiClient.get('API/1_0/status.php.php');

export const getAccountPackages = (account_id) =>
  apiClient.get('API/1_0/getAccountPackage.php', { params: { account_id } });

export const postAccountPackage = (account_id, product_package_id) =>
  apiClient.post('API/1_0/postAccountPackageChange.php', {
    account_id,
    product_package_id,
  });

export const getAccountProductBrands = (account_id) =>
  apiClient.get('API/1_0/getAccountProductBrands.php', {
    params: { account_id },
  });

export const createStripeSetupIntent = (params) =>
  apiClient.post('API/1_0/stripeSetupIntent.php', params);

export const getStripeSetupIntent = (query) =>
  apiClient.get('API/1_0/stripeSetupIntent.php', { params: query });

export const deleteStripeSetupIntent = (id) =>
  apiClient.delete('API/1_0/stripeSetupIntent.php', {
    params: { id, cancellation_reason: 'requested_by_customer' },
  });

export const getStripePaymentMethods = (query) =>
  apiClient.get('API/1_0/stripePaymentMethod.php', { params: query });

export const updatePaymentMethod = (id, values) =>
  apiClient.post('API/1_0/stripePaymentMethod.php', { id, ...values });

export const deletePaymentMethod = (id) =>
  apiClient.delete('API/1_0/stripePaymentMethod.php', { params: { id } });

export const addAccountMeta = (data) =>
  apiClient.post('API/1_0/accountMeta.php', data);

export const provisionTrialNumber = (area_code) =>
  apiClient.post('API/1_0/provisionTrialNumber.php', { area_code });
